import useMock from "@/live/composables/use-mock";
import type {
    DetailOutcomeGroup,
    EventsDrilldownDetailEvent,
    EventsDrilldownDetailEventMarket,
    EventsDrilldownDetailEventMarketOutcome,
    EventsDrilldownDetailEventMarketOutcomeWrapper,
} from "@/live/model/events-drilldown-detail-event";
import { EventsDrilldownDetailEventCollection } from "@/live/model/events-drilldown-detail-event";
import { EmptyMarket, MarketGroup, MarketPayload } from "@/shared/model/market";
import { SubType2Letter } from "@/shared/model/enums/type";
import { GroupCode } from "@/shared/model/enums/group-code";
import { LiveSportsDrilldownNode } from "@/live/model/live-sports-drilldown-nodes";
import type { Event, EventDrilldownDetail } from "@/shared/model/event";
import { DrilldownNode } from "@/shared/model/drilldown-node";
import { Sport } from "@/shared/model/enums/sport";
import { cloneDeep as _cloneDeep, sortBy as _sortBy } from "lodash-es";
import type { PreviewParam } from "@/shared/model/grouping";
import useUtils from "@/shared/composables/use-utils";
import type { OutcomeMarket } from "@/shared/model/outcome";
import { Team } from "@/shared/model/team";

const { getGroupingConfig, sportIsCustomGrouped, getMarketPreviewConfig } = useMock();
const { sortByDisplayOrder } = useUtils();

const groupingConfig = getGroupingConfig();
const detailConfig = environment.config["market-detail"];

function getAllMarketTypes(
    events: EventsDrilldownDetailEvent[],
    prematch: boolean = false,
    sportConfig: PreviewParam[],
): MarketPayload[] {
    const configMarketTypes = [];
    const allMarketTypes = [];

    if (sportConfig.length) {
        for (const group of sportConfig) {
            configMarketTypes.push({
                groupCode: group.fa_code,
                name: group.name,
                displayColumnIndex: null,
                columns: group.columns || null,
                labels: group.labels || null,
                specialLayout: group.specialLayout || false,
            });
        }
    }

    for (const event of events || []) {
        if (!event.groupedMarkets) return [];
        for (const group of event.groupedMarkets) {
            const groupIsIn = allMarketTypes.map((item) => item.groupCode).indexOf(group.groupCode);
            if (groupIsIn < 0) {
                const exactConfig = sportConfig.find((item) => item.fa_code === group.groupCode);
                allMarketTypes.push({
                    groupCode: group.groupCode,
                    displayOrder: group.displayOrder || group.marketDisplayOrder,
                    name: exactConfig?.name || group.name || group.market_name || group.marketName,
                    displayColumnIndex: null,
                    columns: exactConfig?.columns || null,
                    labels: exactConfig?.labels || null,
                    specialLayout: exactConfig?.specialLayout || false,
                });
            }
        }
    }

    if (!allMarketTypes.length) {
        return [];
    }

    const allMarketTypesSorted = sportConfig.length
        ? configMarketTypes.filter((configMarket) =>
              allMarketTypes.some((marketType) => marketType.groupCode === configMarket.groupCode),
          )
        : sortByDisplayOrder(allMarketTypes);

    let allMarketTypesFiltered = [];

    if (prematch && !sportConfig.length) {
        allMarketTypesFiltered = [allMarketTypesSorted[0]];
    } else {
        for (const marketType of allMarketTypesSorted) {
            for (const configGroup of sportConfig) {
                if (marketType.groupCode === configGroup.fa_code) {
                    allMarketTypesFiltered.push(marketType);
                }
            }
            if (!sportConfig.length) {
                allMarketTypesFiltered = allMarketTypesSorted.slice(0, 2);
            }
        }
    }

    return allMarketTypesFiltered;
}

export default function useMarketGrouping() {
    const getHeaders = (
        activeSports: LiveSportsDrilldownNode[],
        sport: LiveSportsDrilldownNode | DrilldownNode,
        refresh = false,
        prematch = false,
        nodeEvents?: EventDrilldownDetail[],
    ): MarketPayload[] => {
        let events = [];
        if (!prematch) {
            events = sport?.events || [];
        } else {
            // Agregate events from loaded leagues for collection of market types
            events = nodeEvents;
        }
        const currentSportConfig = getMarketPreviewConfig(sport.code);
        let allMarketTypes = getAllMarketTypes(events, prematch, currentSportConfig);

        //for some reason the markets are not sorted after last updates - added this setting, please watch it carefully, then remove it
        //allMarketTypes.sort((a, b) => a.displayOrder - b.displayOrder);
        // If there is no config, use only first by market order for chips/market dropdowns
        if (!currentSportConfig.length) {
            //FIXME: For some reason, there WAS allMarektType.length > 1. I have no idea why but seems like a nonsense. Might find out later tho, leaving here the comment
            allMarketTypes = allMarketTypes.length > 0 ? [allMarketTypes[0]] : [];
        }

        if (!refresh) {
            if (allMarketTypes[0]) {
                allMarketTypes[0].displayColumnIndex = 0;
            }
            if (allMarketTypes[1]) {
                allMarketTypes[1].displayColumnIndex = 1;
            }
            if (allMarketTypes[2]) {
                allMarketTypes[2].displayColumnIndex = 2;
            }
        } else {
            allMarketTypes.forEach((marketType, index) => {
                if (marketType) {
                    let existingMarketType;

                    for (const activeSport of activeSports) {
                        existingMarketType = activeSport.oddsHeaders?.find(
                            (header) => header.groupCode === marketType.groupCode,
                        );

                        if (existingMarketType) {
                            break;
                        }
                    }

                    if (existingMarketType) {
                        marketType.displayColumnIndex = existingMarketType.displayColumnIndex;
                    } else {
                        marketType.displayColumnIndex = index;
                    }
                }
            });
        }
        return allMarketTypes;
    };

    function filledInOutcomes(outcomes: (OutcomeMarket | EmptyMarket)[], labels: string[]) {
        const labelsObj = {};
        // If there are no labels but you still managed to get here, go back.
        if (labels?.length === 0) return outcomes.toSorted((a, b) => a.displayOrder - b.displayOrder);

        // Create object from labels array, using _ as prefix because labels are numbers and this is an object.
        labels.forEach((label) => {
            labelsObj[`_${label}`] = null;
        });

        for (const outcome of outcomes) {
            let key = null;

            // Translating in case there are different values in name of outcome. Usually if outcome.type is not DC.
            const dict = {
                H: "1",
                A: "2",
                D: "0",
            };

            if (outcome.type !== "DC") {
                key = `_${dict[outcome.subType]}`;
            } else {
                key = `_${outcome.name}`;
            }

            // Assign to labels object using key from name or subtype.
            labelsObj[key] = outcome;
        }

        // Now we have a labels object from all outcomes, keys being derived from outcome.name or subtype.

        const newOutcomes = [];

        // We started with labelsObj from ['1', '10', '0', '02', '2'] or ['1, '0', '2'] etc, defined in market preview config.
        // If outcome with predefined label was not found, use { empty: true } as outcome.
        for (const label in labelsObj) {
            if (!labelsObj[label]) {
                labelsObj[label] = { empty: true };
            }

            newOutcomes.push(labelsObj[label]);
        }

        // newOutcomes is array of all outcomes

        let allEmpty = true;
        let allInactive = true;

        for (const out of newOutcomes) {
            if (!out.empty) {
                allEmpty = false;
            }

            if (out.active && out.status === "ACTIVE") {
                allInactive = false;
            }
        }

        // If all are empty or inactive, return empty array. Shows "zobrazit ostatni príležitosti" in wrapper.
        if (allEmpty || allInactive) return [];
        return newOutcomes;
    }

    const getOddsByHeaders = (
        sport: LiveSportsDrilldownNode | DrilldownNode,
        event: EventsDrilldownDetailEvent,
        prematch = false,
    ): EventsDrilldownDetailEventMarketOutcome[][] | EventsDrilldownDetailEventMarketOutcomeWrapper => {
        const displayOdds = [];
        const currentSportConfig = getMarketPreviewConfig(sport.code);
        const copyOddsHeaders = _sortBy(_cloneDeep(sport.oddsHeaders) as MarketPayload[], ["displayColumnIndex"]);
        const headers = prematch
            ? copyOddsHeaders
            : copyOddsHeaders?.filter((item) => item.displayColumnIndex !== null);
        if (prematch) {
            const result = {};
            if (!headers.length) {
                const exactConfig = currentSportConfig[0];
                if (!exactConfig) return [];
                result[exactConfig.fa_code] = [];
                const findGroupedMarket = event.groupedMarkets.find(
                    (market) => market.groupCode === exactConfig.fa_code,
                );
                const outcomes = (findGroupedMarket?.outcomes || []).toSorted(
                    (a, b) => a.displayOrder - b.displayOrder,
                );

                if (exactConfig.firstByMarketOrder) {
                    // TODO, potrebujeme pridat do parentMarketu displayOrder marketu.
                    const firstOutcome = outcomes[0];
                    const secondOutcome = outcomes.find(
                        (out) => out.handicapValue === firstOutcome.handicapValue && out.name !== firstOutcome.name,
                    );
                    result[exactConfig.fa_code] = [firstOutcome, secondOutcome].toSorted(
                        (a, b) => b.displayOrder - a.displayOrder,
                    );
                } else if (exactConfig.specialLayout) {
                    result[exactConfig.fa_code] = filledInOutcomes(outcomes, exactConfig.labels);
                } else {
                    result[exactConfig.fa_code] = outcomes;
                }
            } else {
                headers.forEach((header) => {
                    const exactConfig = currentSportConfig.find((item) => item.fa_code === header.groupCode);
                    result[header.groupCode] = [];
                    const findGroupedMarket = event.groupedMarkets.find(
                        (market) => market.groupCode === header.groupCode, // TODO co custom?
                    );
                    const outcomes = (findGroupedMarket?.outcomes || []).toSorted(
                        (a, b) => a.displayOrder - b.displayOrder,
                    );

                    if (exactConfig?.specialLayout) {
                        result[header.groupCode] = filledInOutcomes(outcomes, exactConfig.labels);
                        return;
                    }

                    //addded the condition "outcomes[0].handicapValue to prevent displaying lot of outcomes in one row. We want to treat handicaps as firstByMarketOrder ALWAYS"
                    //I might be wrong with this condition but it seems logical
                    //Switched to !== undefined cuz can be 0
                    if (exactConfig?.firstByMarketOrder || outcomes?.[0]?.handicapValue !== undefined) {
                        // TODO, potrebujeme pridat do parentMarketu displayOrder marketu.
                        const firstOutcome = outcomes.reduce((lowest, out) => {
                            return out.displayOrder < lowest.displayOrder ? out : lowest;
                        }, outcomes[0]);

                        const secondOutcome = outcomes.find(
                            (out) => out.handicapValue === firstOutcome.handicapValue && out.name !== firstOutcome.name,
                        );
                        const thirdOutcome = outcomes.find(
                            (out) =>
                                out.handicapValue === firstOutcome.handicapValue &&
                                out.name !== firstOutcome.name &&
                                out.name !== secondOutcome.name,
                        );

                        let sortedOutcomes = sortOutcomesGroup(
                            [firstOutcome, secondOutcome, thirdOutcome]
                                .filter(Boolean)
                                .toSorted((a, b) => a.displayOrder - b.displayOrder),
                        );

                        // Check if the configuration specifies the number of columns
                        if (exactConfig?.columns) {
                            sortedOutcomes = sortedOutcomes.slice(0, exactConfig.columns);
                        }

                        result[header.groupCode] = sortedOutcomes;
                        return;
                    }

                    result[header.groupCode] = outcomes;
                });
            }
            return result;
        }

        headers.forEach((header) => {
            if (!event.groupedMarkets) {
                displayOdds.push([]);
            }
            const marketGroup = event.groupedMarkets.find((item) => item.groupCode === header.groupCode);
            if (!marketGroup) {
                return displayOdds.push([]);
            }

            if (!marketGroup.outcomes.length) {
                displayOdds.push([]);
                return;
            }
            const lowestOrderOutcomeMarketId = marketGroup.outcomes[0].marketId;

            let outcomes;

            if (!prematch) {
                //The DC fitler on the lowestOrderedOutcomeMarketId is very experimental but fastest solution I came up with. If 10 12 02 are not showing in some cases, might be caused by this.
                //This feature was created to prevent showing DC markets on live preview when there are no outcomes for classic 1 0 2. OddsHeaders are (I hope) used only for the "wrapper", not on detail. This should work, but who knows, if you are reading this, it might not.

                outcomes = marketGroup.outcomes.filter(
                    (item) => item.marketId === lowestOrderOutcomeMarketId && item.type != "DC",
                );
            } else {
                outcomes = marketGroup.outcomes.filter((item) => item.marketId === lowestOrderOutcomeMarketId);
            }

            if (outcomes.length === 1) {
                // SECOND IN PAIR FOR EMPTY LAYOUT STRUCTURE
                outcomes.push({
                    empty: true,
                    displayOrder: outcomes[0].displayOrder === 1 ? 2 : 1,
                } as EmptyMarket);
            }

            displayOdds.push(_sortBy(outcomes, ["displayOrder"]));
        });
        return displayOdds;
    };

    const customGrouping = {
        groupCodes: [GroupCode.MatchResult, GroupCode.DoubleChance],
        group_code: GroupCode.Custom,
        groupCode: GroupCode.Custom,
        fa_code: GroupCode.MatchResult,
        sort_code: SubType2Letter.Mr,
        name: "Zápas",
        market_name: "Zápas",
    };

    function configureOutcomes(
        market: EventsDrilldownDetailEventMarket,
        isCustomGrouped: boolean,
    ): EventsDrilldownDetailEventMarketOutcome[] {
        const outcomes = [];
        for (const outcome of market.outcomes) {
            const o = { ...outcome };
            o.handicapValue = market.handicapValue;
            o.name =
                !market.handicapValue || o.name.endsWith(String(market.handicapValue))
                    ? o.name
                    : `${o.name} ${market.handicapValue}`;
            o.parentMarket = {
                handicapValue: market.handicapValue,
                eventId: market.eventId,
                active: market.active,
                status: market.status,
            };

            // ONLY Vitez 1x2 AND Dvojtip
            if (isCustomGrouped) {
                o.name = updateCustomGroupedOutcomeName(o);
            }

            outcomes.push(o);
        }
        return outcomes;
    }

    function getGrouping(
        sportCode: Sport,
        groupCode: GroupCode,
    ): { groupingHold: MarketGroup; isCustomGrouped: boolean } {
        let grouping = groupingConfig.grouped_markets.find((g) => g.group_code === groupCode);
        if (grouping) {
            grouping.groupCode = grouping.group_code;
        }
        // SOME CUSTOM SPORTS HAVE CUSTOM GROUPING AS IN customGrouping var - BOOL IS NEEDED, OUTCOMES OF CUSTOM GROUPED MARKETS MAY NEED THEIR NAME CHANGED
        let isCustomGrouped = false;

        // MATCH RESULT & DOUBLE_CHANCE ARE NOT IN CONFIG, IF THE MARKET IS ONE OF THESE, SUBSTITUTE grouping WITH customGrouping.
        // AS IN DOCS - Groupování Vítěz (1X2) a Dvojtip

        // TWO DIFFERENT GROUP_CODES/SORT_CODES - nechal jsem MATCH_RESULT a MR, oba groupCody stejne nalezi pod customGrouping a vzdy kdyz k tomuto u
        // sportu dojde, tak se groupujou.
        if (!grouping && sportIsCustomGrouped(sportCode) && customGrouping.groupCodes.includes(groupCode)) {
            if (sportCode === Sport.IceHockey) {
                customGrouping.groupCode = GroupCode.MatchResultNoOvertime;
            }
            grouping = customGrouping;
            isCustomGrouped = true;
        }

        return { groupingHold: grouping, isCustomGrouped };
    }

    const groupMarkets = (
        event: EventsDrilldownDetailEvent | Event,
        groupingLive: boolean,
        sportCode?: Sport,
    ): Array<EventsDrilldownDetailEventMarket | MarketGroup> => {
        const groupedMarkets: Array<EventsDrilldownDetailEventMarket | MarketGroup> = [];
        const hold = {};
        // GOING THROUGH MARKETS OF EVENT

        for (const market of event?.markets || []) {
            if (groupingLive && !market.betInRun) {
                continue;
            }

            // FIND groupCode IN CONFIGURATION
            const { groupingHold, isCustomGrouped } = getGrouping(sportCode, market.groupCode);
            if (!groupingHold) {
                // IF IS NOT SUPPOSED TO BE GROUPED - PUSH AS WHOLE GROUP
                // UNIFY MARKET AND GROUPED_MARKET? - ALL NEEDED DATA IS IN BOTH. marketId & eventId EITHER IN MARKET OR IN outcome.parentMarket
                groupedMarkets.push({ ...market, outcomes: configureOutcomes(market, true) }); //added true to rename 1 X 2 outcomes
                continue;
            }

            // MAKE UNREFERENCED COPY FOR EACH MARKET, OTHERWISE ALL OUTCOMES FROM LAST MARKET ARE IN EVERY MARKET
            const grouping = _cloneDeep(groupingHold) as MarketGroup;
            // CHECK IF GROUP IN ALREADY GROUPED MARKETS EXISTS
            const hasGroupPrepared = Object.keys(hold).includes(grouping.groupCode);

            if (!hasGroupPrepared) {
                hold[grouping.groupCode] = grouping;
                hold[grouping.groupCode].outcomes = [] as EventsDrilldownDetailEventMarketOutcome[];
                hold[grouping.groupCode].marketDisplayOrder = market.displayOrder;
                hold[grouping.groupCode].eventId = event.id;
                hold[grouping.groupCode].isGrouped = true;
            }

            // GROUP TAKES LOWEST displayOrder VALUE FROM MARKETS IN GROUP
            if (hold[grouping.groupCode].marketDisplayOrder > market.displayOrder) {
                hold[grouping.groupCode].marketDisplayOrder = market.displayOrder;
            }

            // GET CONFIGURED OUTCOMES FOR GROUPING OF CURRENT MARKET
            hold[grouping.groupCode].outcomes = hold[grouping.groupCode].outcomes.concat(
                configureOutcomes(market, isCustomGrouped),
            );

            // MISSING OUTCOME FOR EMPTY SPACE
            // TODO - PROVĚŘIT KVŮLI FEBL-118, pravděpodobně už není potřeba, začali jsme to šetřit jinde?????
            // if (market.outcomes.length < 2) {
            //     const emptyOutcome = { empty: true, from: "test" };
            //     hold[grouping.groupCode].outcomes.push(emptyOutcome);
            // }
        }

        // FOR EVERY GROUPED MARKET
        for (const key in hold) {
            groupedMarkets.push(hold[key]);
        }
        return groupingLive
            ? groupedMarkets
            : groupedMarkets.toSorted(
                  (a, b) => a.displayOrder || a.marketDisplayOrder - b.displayOrder || b.marketDisplayOrder,
              );
    };

    const updateCustomGroupedOutcomeName = (outcome: EventsDrilldownDetailEventMarketOutcome): string => {
        if (
            Object.prototype.hasOwnProperty.call(renamingConfig, outcome.type) &&
            Object.prototype.hasOwnProperty.call(renamingConfig[outcome.type], outcome.subType)
        ) {
            // Podle dominika se jmena prepisuji na cislo, jmeno marketu je vysledek zapasu.
            return renamingConfig[outcome.type][outcome.subType];
        }
        return outcome.name;
    };

    const orderOutcomesInGroup = (
        group: EventsDrilldownDetailEventMarket | MarketGroup,
    ): EventsDrilldownDetailEventMarketOutcome[][] => {
        const outcomes = group.outcomes;
        let orderedOutcomes = [];
        const marketsDict = {};

        for (const outcome of outcomes) {
            if (Object.prototype.hasOwnProperty.call(marketsDict, outcome.marketId)) {
                marketsDict[outcome.marketId].push(outcome);
            } else {
                marketsDict[outcome.marketId] = [outcome];
            }
        }

        for (const key of Object.keys(marketsDict)) {
            if (marketsDict[key].length === 1 && !marketsDict[key][0].empty) {
                marketsDict[key].push({
                    empty: true,
                    displayOrder: marketsDict[key][0].displayOrder === 1 ? 2 : 1,
                } as EmptyMarket);
            }

            // Razeni outcomu ve skupine(na radku) outcomu (aby nad bylo prvni a pod bylo druhe napr.)
            marketsDict[key].sort((a, b) => parseInt(a.displayOrder) - parseInt(b.displayOrder));
            if (marketsDict[key].length === 1 && marketsDict[key][0].empty) {
                continue;
            }
            orderedOutcomes.push(marketsDict[key]);
        }

        orderedOutcomes = orderedOutcomes.toSorted((a, b) => a[0].handicapValue - b[0].handicapValue);

        return orderedOutcomes;
    };

    const isGroupOverUnder = (group: EventsDrilldownDetailEventMarket | MarketGroup): boolean => {
        // TODO: experimental feature but it is impossible to catch every groupCode in the config. This is a solution that works for now but will break if f.e. lang changes.
        const overUnderPattern = /^(Nad|Pod) \d+(\.\d+)?$/;

        return group.outcomes.every((outcome) => overUnderPattern.test(outcome.name));
    };

    const isGroupYesNo = (group: EventsDrilldownDetailEventMarket | MarketGroup): boolean => {
        //same experimental bs as above
        return group.outcomes.every(
            (outcome) => outcome.name.toLowerCase() === "ano" || outcome.name.toLowerCase() === "ne",
        );
    };

    // TODO MIDDLEWARE - zmena prejmenovavani abychom nepotrebovali teams?
    const orderOutcomesInToGroups = (
        group: EventsDrilldownDetailEventMarket | MarketGroup,
        teams,
        code: string = null,
    ): DetailOutcomeGroup[] => {
        const outcomes = group.outcomes;
        let orderedOutcomes = [];
        const marketsDict = {};
        let isCounter = false;
        let isDuel = false;
        let isMultigol = false;
        let isStandard = true;
        let isOrderedLabeledTeams = false;
        let isOrderedLabeledGoals = false;

        if (detailConfig.counter.includes(group.groupCode)) {
            isCounter = true;
        }

        if (detailConfig.duel.includes(group.groupCode)) {
            isDuel = true;
        }

        if (detailConfig.multigol.includes(group.groupCode)) {
            isMultigol = true;
        }

        if (detailConfig.orderedLabeledTeam.includes(group.groupCode)) {
            isOrderedLabeledTeams = true;
        }

        if (
            detailConfig.orderedLabeledGoals.includes(group.groupCode) ||
            isInCheckedOrderedLabeledGoals(group.groupCode)
        ) {
            isOrderedLabeledGoals = true;
        }

        if (
            detailConfig.orderingConfig[group.groupCode] ||
            isInCheckedOrdered(group.groupCode) ||
            detailConfig.matchResult.includes(group.groupCode) ||
            isDuel ||
            isOrderedLabeledTeams ||
            isOrderedLabeledGoals ||
            isGroupOverUnder(group) ||
            isGroupYesNo(group)
        ) {
            isStandard = false;
        }

        // Create dictionary of marketId: [outcomes] within group (groupedMarket)
        for (const outcome of outcomes) {
            if (Object.prototype.hasOwnProperty.call(marketsDict, outcome.marketId)) {
                marketsDict[outcome.marketId].push(outcome);
            } else {
                marketsDict[outcome.marketId] = [outcome];
            }
        }

        // For each marketId in marketsDict - ONLY FOR STANDART MARKET DISPLAY
        for (const key of Object.keys(marketsDict)) {
            // Standart markets sort by decimal price

            if (isStandard) {
                marketsDict[key].sort((a, b) => b.prices?.[0]?.decimal - a.prices?.[0]?.decimal);
            }

            // Push as a row into orderedOutcomes
            orderedOutcomes.push(marketsDict[key]);
        }

        // NON STANDART BRANCH (ORDERED, ORDERED WITH LABEL)
        if (!isStandard) {
            // TODO MIDDLEWARE - zmena prejmenovavani abychom nepotrebovali teams?
            if (detailConfig.matchResult.includes(group.groupCode)) {
                //Order MATCH result outcomes by types ("Neprohra" + result)
                orderedOutcomes = orderedOutcomes.sort((a, b) => {
                    const typeOrder = { MR: 1, DC: 2 }; // Define the order of types
                    const typeA = typeOrder[a[0].type] || 2; // Default to 2 if type is not found
                    const typeB = typeOrder[b[0].type] || 2;
                    return typeA - typeB;
                });
                return renameOutcomes(orderedOutcomes, teams).map((outcomeGroup) => ({
                    outcomes: outcomeGroup.sort((a, b) => a.displayOrder - b.displayOrder),
                    isMatchResult: true,
                }));
            }

            if (isDuel) {
                return orderedOutcomes?.map((outcomeGroup) => ({
                    outcomes: outcomeGroup.sort((a, b) => a.displayOrder - b.displayOrder),
                    isDuel: true,
                }));
            }

            if (isOrderedLabeledTeams || isOrderedLabeledGoals) {
                const outcomesByTeams = {};
                if (!orderedOutcomes[0]) return [];
                for (const outcome of orderedOutcomes[0]) {
                    if (outcomesByTeams[outcome.subType]) {
                        outcomesByTeams[outcome.subType].outcomes.push(outcome);
                    } else {
                        outcomesByTeams[outcome.subType] = {
                            outcomes: [outcome],
                            label: isOrderedLabeledTeams && teams ? getTeamName(outcome.subType, teams) : null,
                        };
                    }
                }

                //Sort outcomes - primary sorting for teamed markets is by decimal price, some markets have additional sorting by displayOrder (or other rules)
                //TODO: Create config? for additional sorting rules
                for (const key of Object.keys(outcomesByTeams)) {
                    if (isOrderedLabeledTeams) {
                        outcomesByTeams[key].outcomes.sort((a, b) => a.prices[0].decimal - b.prices[0].decimal);
                    }
                    if (isOrderedLabeledGoals) {
                        //was by displayOrder, reworked to prices  .sort((a, b) => a.displayOrder - b.displayOrder)
                        outcomesByTeams[key].outcomes.sort((a, b) => a.prices[0].decimal - b.prices[0].decimal);

                        //belowe edge cases where business want to sort by displayOrder
                    }
                    if (isOrderedLabeledGoals && group.groupCode?.includes("TOTAL_NUM_GOALS")) {
                        outcomesByTeams[key].outcomes.sort((a, b) => a.displayOrder - b.displayOrder);
                    }
                }

                //sort by TEAMS - home always first
                const sortedOutcomesByTeams = {};
                if (outcomesByTeams["H"]) {
                    sortedOutcomesByTeams["H"] = outcomesByTeams["H"];
                }
                if (outcomesByTeams["A"]) {
                    sortedOutcomesByTeams["A"] = outcomesByTeams["A"];
                }
                for (const key of Object.keys(outcomesByTeams)) {
                    if (key !== "H" && key !== "A") {
                        sortedOutcomesByTeams[key] = outcomesByTeams[key];
                    }
                }

                return [
                    {
                        outcomes: [],
                        outcomesByTeams: sortedOutcomesByTeams,
                        isLabeledByTeams: !!teams,
                        showAll: false,
                    },
                ];
            }

            // Sort rows of outcomes by handicapValue, so that handicap -1 is above 0 and +1.
            orderedOutcomes = orderedOutcomes.toSorted((a, b) => a[0].handicapValue - b[0].handicapValue);
            const holdGroup = [];
            // default columns are used for matched group codes from isInChecked(), because these are not in ordering config, but should be ordered, but there is too many of them.
            // HANDICAP and GOALS_OVER/UNDER mostly. HANDICAP is 3 columns, but ASIAN HANDICAP is 2 columns, and GOALS_OVER/UNDER are 2.
            const defaultColumns = 2;
            // if (group.groupCode.includes("HANDICAP") && !group.groupCode.includes("ASIAN")) {
            //     if (!code) {
            //         //   defaultColumns = 3;
            //     }
            // }

            // Going through rows for filling empty outcomes.
            for (const outcomesGroup of orderedOutcomes) {
                if (
                    (detailConfig.orderingConfig[group.groupCode] ||
                        isInCheckedOrdered(group.groupCode) ||
                        isInCheckedOrderedLabeledGoals(group.groupCode) ||
                        isGroupOverUnder(group) ||
                        isGroupYesNo(group)) &&
                    outcomesGroup.length !== (detailConfig.orderingConfig[group.groupCode]?.columns || defaultColumns)
                ) {
                    // Get all display orders in current row
                    const displayOrders = outcomesGroup.map((item) => item.displayOrder);
                    // Filling in empty outcomes.
                    for (
                        let i = 1;
                        i <= (detailConfig.orderingConfig[group.groupCode]?.columns || defaultColumns);
                        i++
                    ) {
                        //Added check about columns - seems like the logic is "show empty spaces only if there are less than 3 columns" - will try, leaving the comment here, in case it is wrong remove the detailConfig if statement
                        if (
                            !detailConfig.orderingConfig[group.groupCode]?.columns ||
                            detailConfig.orderingConfig[group.groupCode]?.columns < 3
                        ) {
                            if (!displayOrders.includes(i)) {
                                // Empty outcome to keep the space when outcome with missing displayOrder.
                                outcomesGroup.push({
                                    empty: true,
                                    displayOrder: i,
                                } as EmptyMarket);
                            }
                        }
                    }
                }
                outcomesGroup.sort((a, b) => a.displayOrder - b.displayOrder);

                //additional sorting logic for HANDICAP markets
                //ufc-mma group does not have group.groupCode sometimes - be aware
                outcomesGroup.sort((a, b) => {
                    if (group.groupCode?.includes("HANDICAP")) {
                        const subTypeOrder = { H: 1, L: 2, A: 3 };
                        const subTypeA = subTypeOrder[a.subType] || 4;
                        const subTypeB = subTypeOrder[b.subType] || 4;

                        if (subTypeA !== subTypeB) {
                            return subTypeA - subTypeB;
                        }
                    }
                    return a.displayOrder - b.displayOrder;
                });

                outcomesGroup.value = sortOutcomesGroup(outcomesGroup);
                // This is a row of outcomes. For ordered display we have specified number of columns. Each element of array holdGroup is one row.
                holdGroup.push({ outcomes: outcomesGroup });
            }
            return holdGroup;
        }

        // STANDART DISPLAY BRANCH

        // Standart display is all in one row, flex wrapping.
        const flattenedOutcomes = [[]] as EventsDrilldownDetailEventMarketOutcome[][];
        for (const group of orderedOutcomes) {
            for (const outcome of group) {
                flattenedOutcomes[0].push(outcome);
            }
        }
        let sortedFlattenedOutcomes = [];

        // Multigol type sorting by displayOrder. Multigol boolean means it should be in outcomes of 4 per row.
        if (isMultigol) {
            sortedFlattenedOutcomes = flattenedOutcomes[0].toSorted((a, b) => a.displayOrder - b.displayOrder);
        } else {
            //Custom sorting rules incoming
            sortedFlattenedOutcomes = flattenedOutcomes[0].toSorted((a, b) => {
                const aName = a.name.toLowerCase();
                const bName = b.name.toLowerCase();

                if (flattenedOutcomes[0].length === 2) {
                    if (aName === "ano" && bName === "ne") {
                        return -1; // "ano" comes before "ne"
                    } else if (aName === "ne" && bName === "ano") {
                        return 1; // "ne" comes after "ano"
                    }
                }

                if (a.displayOrder !== b.displayOrder) {
                    return a.displayOrder - b.displayOrder; // Sort by displayOrder first
                }
                return a.prices[0].decimal - b.prices[0].decimal; // Default sorting by decimal if displayOrder is the same
            });
        }

        if (isCounter) {
            const tempInitial = sortedFlattenedOutcomes[0];

            if (tempInitial) {
                // const groupedOutcomes = (() => {
                //     const rows: any[] = [];

                //     // Step 1: Normalize score combinations
                //     const normalizeScore = (home: number, away: number) => {
                //         if (home > away) return `${home}-${away}`;
                //         if (away > home) return `${away}-${home}`;
                //         return `${home}-${home}`; // For draws
                //     };

                //     const outcomesByNormalizedScore: Record<string, typeof sortedFlattenedOutcomes> = {};

                //     sortedFlattenedOutcomes.forEach((outcome) => {
                //         const scoreKey = normalizeScore(outcome.outcomeScore?.home, outcome.outcomeScore?.away);
                //         if (!outcomesByNormalizedScore[scoreKey]) {
                //             outcomesByNormalizedScore[scoreKey] = [];
                //         }
                //         outcomesByNormalizedScore[scoreKey].push(outcome);
                //     });

                //     // Step 2: Dynamically create rows with proper alignment
                //     const processedScores = new Set<string>();

                //     Object.entries(outcomesByNormalizedScore).forEach(([scoreKey, outcomes]) => {
                //         const [home, away] = scoreKey.split("-").map(Number);

                //         // Skip processing if this score has been handled as part of another row
                //         if (processedScores.has(scoreKey)) return;

                //         // Find Team A, Draw, and Team B outcomes
                //         const teamAOutcome = outcomes.find((o) => o.outcomeScore.home > o.outcomeScore.away);
                //         const remizaOutcome = outcomesByNormalizedScore[`${home}-${home}`]?.find(
                //             (o) => o.outcomeScore.home === o.outcomeScore.away,
                //         );
                //         const teamBOutcome = outcomes.find((o) => o.outcomeScore.home < o.outcomeScore.away);

                //         // Construct row with all outcomes
                //         const rowOutcomes = [
                //             teamAOutcome || { empty: true, displayOrder: 1 },
                //             remizaOutcome || { empty: true, displayOrder: 2 },
                //             teamBOutcome || { empty: true, displayOrder: 3 },
                //         ];

                //         rows.push({ outcomes: rowOutcomes });

                //         // Mark both the "win" and "draw" scores as processed
                //         processedScores.add(scoreKey);
                //         if (remizaOutcome) {
                //             processedScores.add(`${home}-${home}`);
                //         }
                //     });

                //     return rows;
                // })();

                //  const formattedGroups = groupedOutcomes.map((group) => group.outcomes);

                return [
                    {
                        outcomes: sortedFlattenedOutcomes,
                        //groupedOutcomes: formattedGroups, TODO: Later we can use this for displaying outcomes in a specific order in rows
                        multigol: false,
                        isCounter,
                        home: tempInitial.outcomeScore.home,
                        away: tempInitial.outcomeScore.away,
                        showAll: false,
                    },
                ];
            }
        }

        // Returning all outcomes in one row.

        return [
            {
                outcomes: sortedFlattenedOutcomes,
                multigol: isMultigol,
            },
        ];
    };

    function renameOutcomes(outcomes, teams) {
        const homeTeam = teams.find((t) => t.side === "HOME");
        const awayTeam = teams.find((t) => t.side === "AWAY");

        const config = {
            MR: {
                H: `${homeTeam.name}`,
                A: `${awayTeam.name}`,
                D: "Remíza",
            },

            DC: {
                "1": `Neprohra ${homeTeam.name}`,
                "2": `Neprohra ${awayTeam.name}`,
                "3": `Nebude remíza`, // TODO DOTAZ - v original datech chodi pro obyc negroupovany DC market "HOME TEAM nebo AWAY TEAM", co chceme?
            },
        };
        for (const outcomeGroup of outcomes) {
            for (const outcome of outcomeGroup) {
                if (config[outcome.type]?.[outcome.subType]) {
                    outcome.name = config[outcome.type]?.[outcome.subType];
                }
            }
        }

        return outcomes;
    }

    function getTeamName(subType, teams) {
        return teams.find((t) => t.side === subTypeToTeam[subType]).name;
    }

    const subTypeToTeam = {
        A: "AWAY",
        H: "HOME",
    };

    function isInCheckedOrdered(groupCode) {
        for (const str of detailConfig.excludeOrderedCheckingStrings) {
            if (groupCode?.includes(str)) {
                return false;
            }
        }

        for (const str of detailConfig.orderedCheckingStrings) {
            if (groupCode?.includes(str)) return true;
        }

        return false;
    }

    function isInCheckedOrderedLabeledGoals(groupCode) {
        for (const str of detailConfig.orderedLabeledGoalsCheckingStrings) {
            if (groupCode?.includes(str)) {
                return true;
            }
        }
    }

    const renamingConfig = {
        MR: { H: "1", D: "X", A: "2" },
        HH: { H: "1", D: "X", A: "2" },
        DC: { "1": "10", "2": "02", "3": "12" },
    };

    // TODO vymyslet jak z toho udelat composables
    // async function processMarkets(myEvent: Ref<EventsDrilldownDetailEvent>) {
    //     myEvent.value.groupedMarkets = await groupMarkets(myEvent.value, false, myEvent.value.drilldownNodes[0].code);

    //     for (const group of myEvent.value.groupedMarkets) {
    //         group.orderedOutcomes = orderOutcomesInGroup(group);
    //     }
    //     if (!selectedCollection.value) {
    //         myEvent.value.collections.sort((a, b) => (a.displayOrder || 5000) - (b.displayOrder || 5000));
    //         setTimeout(async () => {
    //             await processAllCollections();
    //             collectionSelected(myEvent.value.collections[0].id);
    //         });
    //     }
    // }

    function sortGroupedMarkets(col, groupedMarketsToSort): Array<EventsDrilldownDetailEventMarket | MarketGroup> {
        const groupedMarketsSorted = [];
        for (const market of groupedMarketsToSort) {
            if (market.id && col.marketIds.includes(market.id)) {
                groupedMarketsSorted.push(market);
                continue;
            }

            // Check explicitly for undefined
            if (market.displayOrder === undefined) {
                market.displayOrder = market.marketDisplayOrder;
            }

            const marketIds: string[] = market.outcomes.map((item) => item.marketId);
            for (const id of marketIds) {
                if (col.marketIds.includes(id)) {
                    groupedMarketsSorted.push(market);
                    break;
                }
            }
        }
        return groupedMarketsSorted;
    }

    //duplicate for some handicap sorting above. TODO: merge together, rn i'm too afraid to touch it
    function sortHandicapOutcomes(outcomeGroup) {
        if (!outcomeGroup || !outcomeGroup.length) return outcomeGroup;
        return outcomeGroup.sort((a, b) => {
            const subTypeOrder = { H: 1, L: 2, A: 3 };
            const subTypeA = subTypeOrder[a.subType] || 4;
            const subTypeB = subTypeOrder[b.subType] || 4;

            if (subTypeA !== subTypeB) {
                return subTypeA - subTypeB;
            }

            return a.displayOrder - b.displayOrder;
        });
    }

    async function processAllCollections(
        myEvent: EventsDrilldownDetailEvent,
        collectionsDict: { [key: string]: EventsDrilldownDetailEventCollection },
        favouritesStore,
    ) {
        for (const col of myEvent.collections) {
            const groupedMarketsToSort = [...myEvent.groupedMarkets].filter((market) => market.outcomes.length);
            const groupedMarketsSorted = sortGroupedMarkets(col, groupedMarketsToSort);

            const sortedMarkets = favouritesStore.sortMarkets(groupedMarketsSorted);

            col.marketCount = sortedMarkets.length;
            if (!sortedMarkets.length) {
                continue;
            }
            collectionsDict[col.id] = {
                ...col,
                markets: sortedMarkets,
            };
        }
    }

    const renameMarket = (
        market: EventsDrilldownDetailEventMarket | MarketGroup,
        odd: OutcomeMarket | EmptyMarket,
        teams?: Team[],
    ) => {
        const fulltext = ["MONEY_LINE", "MATCH_WINNER", "WINNER", "SET_WINNER_NTH", "CUSTOM"];
        const contains = ["MATCH_RESULT", "FRAME_WINNER", "MATCH_WINNER", "FIGHT_WINNER"];
        if (!market || !odd) return odd.name;
        const marketCode = market.groupCode;

        //Should not happen but its happening :(
        if (!marketCode) return odd.name;
        if (fulltext.includes(marketCode) || contains.some((code) => marketCode.includes(code))) {
            if (odd.subType === "H") {
                return "1";
            }
            if (odd.subType === "A") {
                return "2";
            } else if (odd.subType === "D") {
                return "0";
            } else {
                //is this required?
                const homeTeam = teams.find((t) => t.side === "HOME");
                const awayTeam = teams.find((t) => t.side === "AWAY");

                if (odd.name.toLowerCase() == homeTeam.name.toLowerCase()) {
                    return "1";
                } else if (odd.name.toLowerCase() == awayTeam.name.toLowerCase()) {
                    return "2";
                }

                return odd.name;
            }
        }
        return odd.name;
    };

    function sortOutcomesGroup(
        outcomesGroup: Array<EventsDrilldownDetailEventMarketOutcome | EmptyMarket>,
    ): Array<EventsDrilldownDetailEventMarketOutcome | EmptyMarket> {
        if (outcomesGroup?.length === 2) {
            return outcomesGroup.toSorted((a, b) => {
                if ("empty" in a && a.empty && !("empty" in b && b.empty)) {
                    return b.name.includes("Nad") ? -1 : 1; // Empty comes first if b is "Nad", otherwise last
                } else if (!("empty" in a && a.empty) && "empty" in b && b.empty) {
                    return a.name.includes("Pod") ? -1 : 1; // Empty comes first if a is "Pod", otherwise last
                } else if ("empty" in a && a.empty && "empty" in b && b.empty) {
                    return 0; // No change if both are empty
                }

                if (!a.name || !b.name) return 0;
                if (a.name.includes("Pod") && a.handicapValue && b.name.includes("Nad") && b.handicapValue) {
                    return -1; // "Pod" comes before "Nad"
                } else if (a.name.includes("Nad") && a.handicapValue && b.name.includes("Pod") && b.handicapValue) {
                    return 1; // "Nad" comes after "Pod"
                }
                return 0; // No change if both are "Pod" or both are "Nad"
            });
        }

        return outcomesGroup;
    }

    return {
        groupMarkets,
        getHeaders,
        filledInOutcomes,
        getOddsByHeaders,
        orderOutcomesInGroup,
        processAllCollections,
        orderOutcomesInToGroups,
        renameMarket,
        sortOutcomesGroup,
        sortHandicapOutcomes,
    };
}
